import React, { useState, useRef, useEffect, useCallback } from 'react';
import html2canvas from 'html2canvas';
import {
  PosterElement, LiveMatch,
  getMatchElements, saveMatchElements, renderMatchPoster,
  getMatchPosterUrl, getProxyImageUrl, uploadMatchEditorImage, uploadMatchPoster, API,
  getPollinationsStyles, getMatchAiImages, generateMatchBackground, setMatchBackgroundFilename,
  uploadMatchBackground,
  type PollinationsStyle, type MatchAiImage,
} from './api';

// ── Constants ──────────────────────────────────────────────────────────────

const CANVAS_W = 1080;
const CANVAS_H = 1920;
const PREVIEW_W = 360;
const PREVIEW_H = Math.round(CANVAS_H * (PREVIEW_W / CANVAS_W)); // 640
const SCALE = PREVIEW_W / CANVAS_W;

const GOOGLE_FONTS = [
  'Dancing Script', 'Pacifico', 'Great Vibes', 'Satisfy', 'Lobster',
  'Righteous', 'Bebas Neue', 'Montserrat', 'Raleway', 'Oswald',
  'Lato', 'Playfair Display', 'Cinzel', 'Exo 2',
];

// ── Helpers ────────────────────────────────────────────────────────────────

let _idCounter = Date.now();
const genId = () => `el_${++_idCounter}`;

function buildDefaultElements(match: LiveMatch): PosterElement[] {
  const scheduled = match.scheduled_at || '';
  let timeStr = '';
  let dateStr = '';
  if (scheduled) {
    const [d, t] = scheduled.split(' ');
    dateStr = d || '';
    if (t) {
      const [hh, mm] = t.split(':');
      timeStr = mm && mm !== '00' ? `${hh}h${mm}` : `${hh}h00`;
    }
  }

  // French date formatting
  const DAYS = ['Lundi','Mardi','Mercredi','Jeudi','Vendredi','Samedi','Dimanche'];
  const MONTHS = ['Janvier','Février','Mars','Avril','Mai','Juin',
                  'Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
  let fmtDate = dateStr;
  try {
    const d = new Date(dateStr);
    fmtDate = `${DAYS[d.getDay() === 0 ? 6 : d.getDay() - 1]} ${d.getDate()} ${MONTHS[d.getMonth()]}`;
  } catch {}

  const matchType = match.match_type;
  // Type de match en image (officiel / anniversaire), variante blanche par défaut.
  // Dimensions calées sur les assets (officiel 250×214, anniversaire 386×220).
  const typeKey = matchType === 'anniversaire' ? 'anniversaire' : 'officiel';
  const typeIsAnniv = typeKey === 'anniversaire';
  const typeEl: PosterElement = {
    id: genId(), type: 'image', role: 'matchtype',
    x: 52, y: 50,
    width: typeIsAnniv ? 460 : 330,
    height: typeIsAnniv ? 262 : 282,
    image_url: `/match-posters/asset_match_${typeKey}_blanc.png`,
    opacity: 1,
  };

  const els: PosterElement[] = [
    // Type de match — image (officiel / anniversaire), noir/blanc sélectionnable
    typeEl,
    // Logo NS Live — top-right (variante blanche par défaut, pour fonds sombres)
    {
      id: genId(), type: 'logo',
      x: 820, y: 30, width: 220, height: 150,
      opacity: 1, logo_variant: 'blanc',
    },
    // Time — right, below logo
    {
      id: genId(), type: 'text', role: 'time',
      x: 540, y: 160, width: 500, height: 120,
      content: timeStr || '21h00',
      font_family: 'Montserrat', font_size: 110, bold: true,
      color: '#ffffff', text_align: 'right',
      shadow_color: '#000000', shadow_offset: 5, opacity: 1,
    },
    // Avatar 1 — left (anneau bleu)
    {
      id: genId(), type: 'avatar1',
      x: 50, y: 1090, width: 330, height: 330,
      border_color: '#1e90ff', border_width: 8, opacity: 1,
    },
    // Avatar 2 — right (anneau rouge)
    {
      id: genId(), type: 'avatar2',
      x: 700, y: 1090, width: 330, height: 330,
      border_color: '#ff3b3b', border_width: 8, opacity: 1,
    },
    // VS — image stylisée (filtre N&B optionnel)
    {
      id: genId(), type: 'image',
      x: 340, y: 1140, width: 400, height: 374,
      image_url: '/match-posters/asset_vs.png',
      opacity: 1, grayscale: false,
    },
    // Name 1 — large cell centered under avatar 1
    {
      id: genId(), type: 'text', role: 'name1',
      x: 0, y: 1435, width: 430, height: 80,
      content: (match.streamer1_name || 'Streamer 1').toUpperCase(),
      font_family: 'Montserrat', font_size: 46, bold: true,
      color: '#ffffff', text_align: 'center',
      shadow_color: '#000000', shadow_offset: 3, opacity: 1,
    },
    // Name 2 — large cell centered under avatar 2
    {
      id: genId(), type: 'text', role: 'name2',
      x: 650, y: 1435, width: 430, height: 80,
      content: (match.streamer2_name || 'Challenger').toUpperCase(),
      font_family: 'Montserrat', font_size: 46, bold: true,
      color: '#ffffff', text_align: 'center',
      shadow_color: '#000000', shadow_offset: 3, opacity: 1,
    },
    // Date — bottom
    {
      id: genId(), type: 'text', role: 'date',
      x: 40, y: 1810, width: 1000, height: 110,
      content: fmtDate || 'Date',
      font_family: 'Montserrat', font_size: 96, bold: true,
      color: '#ff80d0', text_align: 'center',
      shadow_color: '#000000', shadow_offset: 6, opacity: 1,
    },
  ];
  return els;
}

// Réinjecte le contenu dynamique (noms, date, heure, type) d'un match dans un
// template (utilisé quand on applique le "défaut" enregistré à un autre match).
function applyMatchData(template: PosterElement[], match: LiveMatch): PosterElement[] {
  const scheduled = match.scheduled_at || '';
  const [d, t] = scheduled.split(' ');
  let timeStr = '';
  if (t) { const [hh, mm] = t.split(':'); timeStr = mm && mm !== '00' ? `${hh}h${mm}` : `${hh}h00`; }
  const DAYS = ['Lundi','Mardi','Mercredi','Jeudi','Vendredi','Samedi','Dimanche'];
  const MONTHS = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
  let fmtDate = d || '';
  try { const dd = new Date(d); fmtDate = `${DAYS[dd.getDay() === 0 ? 6 : dd.getDay() - 1]} ${dd.getDate()} ${MONTHS[dd.getMonth()]}`; } catch {}
  const typeKey = match.match_type === 'anniversaire' ? 'anniversaire' : 'officiel';
  return template.map(el => {
    const e = { ...el };
    switch (el.role) {
      case 'name1': e.content = (match.streamer1_name || 'Streamer 1').toUpperCase(); break;
      case 'name2': e.content = (match.streamer2_name || 'Challenger').toUpperCase(); break;
      case 'time':  e.content = timeStr || '21h00'; break;
      case 'date':  e.content = fmtDate || 'Date'; break;
      case 'matchtype': {
        // garde la variante noir/blanc choisie, change juste le type
        const variant = (el.image_url || '').includes('_noir') ? 'noir' : 'blanc';
        e.image_url = `/match-posters/asset_match_${typeKey}_${variant}.png`;
        break;
      }
    }
    return e;
  });
}

// ── Sub-components ─────────────────────────────────────────────────────────

function ElementPreview({
  el, selected, scale, match, logoUrl, onPointerDown, editing = true,
}: {
  el: PosterElement;
  selected: boolean;
  scale: number;
  match: LiveMatch;
  logoUrl: string | null;
  onPointerDown?: (e: React.PointerEvent) => void;
  editing?: boolean;
}) {
  const style: React.CSSProperties = {
    position: 'absolute',
    left: el.x * scale,
    top: el.y * scale,
    width: el.width * scale,
    height: el.height * scale,
    opacity: el.opacity ?? 1,
    cursor: editing ? 'move' : 'default',
    boxSizing: 'border-box',
    outline: !editing ? undefined : (selected ? '2px solid #00cfff' : '1px dashed rgba(255,255,255,0.2)'),
    zIndex: selected ? 10 : 1,
    overflow: 'hidden',
    userSelect: 'none',
    transform: el.rotate ? `rotate(${el.rotate}deg)` : undefined,
    transformOrigin: 'center center',
    touchAction: 'none',
  };

  const getAvatarUrl = (type: 'avatar1' | 'avatar2') => {
    const raw = type === 'avatar1' ? match.streamer1_avatar : match.streamer2_avatar_url;
    if (!raw) return null;
    if (raw.startsWith('http')) return getProxyImageUrl(raw);
    return raw;
  };

  if (el.type === 'avatar1' || el.type === 'avatar2') {
    const avatarUrl = getAvatarUrl(el.type);
    const ringCol = el.border_color || (el.type === 'avatar1' ? '#1e90ff' : '#ff3b3b');
    const ringUrl = `${API.defaults.baseURL || ''}/poster-assets/ring?color=${ringCol.replace('#', '')}`;
    // L'anneau est ~1.5× plus grand que l'avatar et déborde de la boîte
    // (overflow visible) pour bien encadrer la photo.
    const ringPct = 150;
    const off = (ringPct - 100) / 2;
    return (
      <div style={{ ...style, padding: 0, overflow: 'visible' }} onPointerDown={onPointerDown}>
        {/* Anneau teinté DERRIÈRE, plus grand que l'avatar */}
        <img src={ringUrl} alt="" crossOrigin="anonymous" style={{ position: 'absolute', left: `-${off}%`, top: `-${off}%`, width: `${ringPct}%`, height: `${ringPct}%`, objectFit: 'fill', pointerEvents: 'none', zIndex: 0 }} />
        {/* Avatar rond PAR-DESSUS */}
        <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', overflow: 'hidden', background: '#3a3a5a', zIndex: 1 }}>
          {avatarUrl ? (
            <img src={avatarUrl} alt="" crossOrigin="anonymous" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontSize: 11 * scale, color: '#aaa' }}>Avatar</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (el.type === 'logo') {
    const variantUrl = el.logo_variant
      ? `${API.defaults.baseURL || ''}/match-posters/asset_logo_${el.logo_variant}.png`
      : (logoUrl ? getProxyImageUrl(logoUrl) : null);
    return (
      <div style={{ ...style, overflow: 'hidden' }} onPointerDown={onPointerDown}>
        {variantUrl ? (
          <img src={variantUrl} alt="Logo"
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
        ) : (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.05)', borderRadius: 4 }}>
            <span style={{ fontSize: 10 * scale, color: '#ccc' }}>Logo</span>
          </div>
        )}
      </div>
    );
  }

  if (el.type === 'image' && el.image_url) {
    const isAsset = el.image_url.startsWith('/match-posters/');
    const base = API.defaults.baseURL || '';
    let imgSrc: string;
    let cssFilter: string | undefined;
    if (el.grayscale && isAsset) {
      // N&B via une image réellement grisée (html2canvas ignore le filtre CSS)
      const fname = el.image_url.replace('/match-posters/', '');
      imgSrc = `${base}/poster-assets/gray/${fname}`;
    } else {
      imgSrc = isAsset ? `${base}${el.image_url}` : el.image_url;
      cssFilter = el.grayscale ? 'grayscale(1)' : undefined; // fallback images externes
    }
    return (
      <div style={style} onPointerDown={onPointerDown}>
        <img src={imgSrc} alt="" crossOrigin="anonymous" style={{ width: '100%', height: '100%', objectFit: 'contain', filter: cssFilter }} />
      </div>
    );
  }

  if (el.type === 'text') {
    const fs = (el.font_size || 48) * scale;
    const shadowStyle = el.shadow_color
      ? `${(el.shadow_offset || 4) * scale}px ${(el.shadow_offset || 4) * scale}px 0 ${el.shadow_color}`
      : undefined;
    const maxW = el.width * scale - 4;
    return (
      <div
        style={{
          ...style,
          display: 'flex',
          alignItems: 'center',
          justifyContent:
            el.text_align === 'center' ? 'center' :
            el.text_align === 'right' ? 'flex-end' : 'flex-start',
          fontFamily: el.font_family && el.font_family !== 'DejaVu' ? `'${el.font_family}', sans-serif` : 'sans-serif',
          fontWeight: el.bold ? 700 : 400,
          fontStyle: el.italic ? 'italic' : 'normal',
          color: el.color || '#ffffff',
          textShadow: shadowStyle,
          padding: '0 2px',
        }}
        onPointerDown={onPointerDown}
      >
        <FitText text={el.content || '(texte vide)'} baseFontPx={fs} maxWidthPx={maxW} />
      </div>
    );
  }

  return <div style={style} onPointerDown={onPointerDown} />;
}

// Télécharge réellement une image (récupère le blob → force le download,
// quels que soient les en-têtes Content-Disposition du serveur).
async function downloadImage(url: string, filename: string) {
  try {
    const resp = await fetch(url, { credentials: 'include' });
    const blob = await resp.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objUrl), 2000);
  } catch {
    // repli : ouvre dans un onglet
    window.open(url, '_blank');
  }
}

// Réduit la taille de police pour que le texte tienne sur une ligne dans la cellule.
function FitText({ text, baseFontPx, maxWidthPx }: { text: string; baseFontPx: number; maxWidthPx: number }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [fontPx, setFontPx] = useState(baseFontPx);
  useEffect(() => {
    const el = ref.current;
    if (!el || maxWidthPx <= 0) { setFontPx(baseFontPx); return; }
    // Mesure à la taille de base puis calcule le facteur de réduction
    el.style.fontSize = `${baseFontPx}px`;
    const natural = el.scrollWidth;
    if (natural > maxWidthPx) {
      setFontPx(Math.max(8, baseFontPx * (maxWidthPx / natural)));
    } else {
      setFontPx(baseFontPx);
    }
  }, [text, baseFontPx, maxWidthPx]);
  return (
    <span ref={ref} style={{ fontSize: fontPx, whiteSpace: 'nowrap', display: 'inline-block', lineHeight: 1.1 }}>
      {text}
    </span>
  );
}

// ── Resize handle directions ───────────────────────────────────────────────

const HANDLES = ['nw','n','ne','e','se','s','sw','w'] as const;
type HandleDir = typeof HANDLES[number];

function ResizeHandles({ el, scale, onHandleDown, big }: {
  el: PosterElement;
  scale: number;
  onHandleDown: (dir: HandleDir, e: React.PointerEvent) => void;
  big?: boolean;
}) {
  const hs = big ? 22 : 8;
  const positions: Record<HandleDir, { left?: number; right?: number; top?: number; bottom?: number; cursor: string }> = {
    nw: { left: -hs/2, top: -hs/2, cursor: 'nw-resize' },
    n:  { left: el.width*scale/2 - hs/2, top: -hs/2, cursor: 'n-resize' },
    ne: { right: -hs/2, top: -hs/2, cursor: 'ne-resize' },
    e:  { right: -hs/2, top: el.height*scale/2 - hs/2, cursor: 'e-resize' },
    se: { right: -hs/2, bottom: -hs/2, cursor: 'se-resize' },
    s:  { left: el.width*scale/2 - hs/2, bottom: -hs/2, cursor: 's-resize' },
    sw: { left: -hs/2, bottom: -hs/2, cursor: 'sw-resize' },
    w:  { left: -hs/2, top: el.height*scale/2 - hs/2, cursor: 'w-resize' },
  };
  return (
    <>
      {HANDLES.map(dir => (
        <div
          key={dir}
          style={{
            position: 'absolute',
            width: hs, height: hs,
            background: '#00cfff',
            border: big ? '2px solid #fff' : '1px solid #003',
            borderRadius: big ? '50%' : 2,
            zIndex: 20,
            pointerEvents: 'auto',
            touchAction: 'none',
            boxShadow: big ? '0 1px 4px rgba(0,0,0,0.4)' : undefined,
            ...positions[dir],
          }}
          onPointerDown={e => { e.stopPropagation(); onHandleDown(dir, e); }}
        />
      ))}
    </>
  );
}

// ── Main PosterEditor component ────────────────────────────────────────────

interface Props {
  match: LiveMatch;
  isAdmin?: boolean;
  isMobile?: boolean;
  onClose: () => void;
  onSaved: (posterFilename: string) => void;
  onBackgroundUpdated?: (backgroundFilename: string) => void;
}

export default function PosterEditor({ match, isAdmin, isMobile, onClose, onSaved, onBackgroundUpdated }: Props) {
  const [elements, setElements] = useState<PosterElement[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [rendering, setRendering] = useState(false);
  const [saved, setSaved] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  // Mobile : tiroir gauche (liste) ouvert ou non
  const [showListMobile, setShowListMobile] = useState(false);
  const [sheetExpanded, setSheetExpanded] = useState(false); // panneau propriétés mobile replié par défaut
  // Feedback "modèle par défaut enregistré"
  const [defaultSaved, setDefaultSaved] = useState(false);
  // Échelle responsive du canvas (le mobile réduit pour tenir à l'écran)
  const [previewScale, setPreviewScale] = useState(SCALE);
  const [renderUrl, setRenderUrl] = useState<string | null>(
    match.poster_filename ? getMatchPosterUrl(match.poster_filename) : null
  );

  // Background URL (from uploaded/generated background image) — state so the
  // IA generation can update it live.
  const [bgFilename, setBgFilename] = useState<string | null>(match.background_filename || null);
  const bgUrl = bgFilename ? getMatchPosterUrl(bgFilename) : null;

  // ── Panneau IA (Pollinations) ──
  const [showAiPanel, setShowAiPanel] = useState(false);

  // ── Upload d'un fond (alternative à l'IA) ──
  const bgFileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingBg, setUploadingBg] = useState(false);
  const handleBgFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadingBg(true);
    try {
      const r = await uploadMatchBackground(match.id, file);
      const filename = r.data.background_filename || r.data.filename;
      setBgFilename(filename);
      onBackgroundUpdated?.(filename);
      if (isMobile) setShowListMobile(false);
    } catch (err: any) {
      alert(err?.response?.data?.detail || "Échec de l'upload du fond.");
    } finally {
      setUploadingBg(false);
    }
  };

  // Calcule l'échelle d'aperçu pour que le canvas tienne à l'écran (mobile).
  useEffect(() => {
    function fit() {
      if (!isMobile) { setPreviewScale(SCALE); scaleRef.current = SCALE; return; }
      const availW = Math.min(window.innerWidth - 16, 460);
      const availH = window.innerHeight - 170; // header + barre d'actions
      const s = Math.min(availW / CANVAS_W, availH / CANVAS_H);
      const clamped = Math.max(0.12, s);
      setPreviewScale(clamped); scaleRef.current = clamped;
    }
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

  // Échelle effective du canvas (lue par les handlers pointer)
  const scaleRef = useRef(SCALE);
  // Drag/resize/rotate state — stored in ref to avoid stale closures
  const dragRef = useRef<{
    type: 'drag' | 'resize' | 'rotate';
    id: string;
    startMX: number; startMY: number;
    origX: number; origY: number;
    origW: number; origH: number;
    dir?: HandleDir;
    centerX?: number;   // screen coords, for rotate
    centerY?: number;
  } | null>(null);

  const canvasRef = useRef<HTMLDivElement>(null);
  const fullRef = useRef<HTMLDivElement>(null);   // conteneur pleine échelle pour la capture
  const centerScrollRef = useRef<HTMLDivElement>(null); // zone scrollable du canvas (mobile)
  const imageFileInputRef = useRef<HTMLInputElement>(null);

  // Load Google Fonts CSS
  useEffect(() => {
    GOOGLE_FONTS.forEach(family => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = `https://fonts.googleapis.com/css2?family=${family.replace(/ /g,'+')}:wght@400;700&display=swap`;
      document.head.appendChild(link);
    });
  }, []);

  // Fetch agency logo URL
  useEffect(() => {
    API.get<{ agency_logo_url: string | null }>('/settings/public')
      .then(r => setLogoUrl(r.data.agency_logo_url))
      .catch(() => {});
  }, []);

  // Load elements from backend (or build defaults)
  useEffect(() => {
    setLoading(true);
    // Retire l'ancien libellé "PRODUCT BY" (désormais inclus dans le logo)
    const stripLegacy = (els: PosterElement[]) =>
      els.filter(el => (el.content || '').trim().toUpperCase() !== 'PRODUCT BY');
    const loadElements = async () => {
      try {
        const r = await getMatchElements(match.id);
        if (r.data && r.data.length > 0) {
          // Resynchronise le contenu dynamique (noms, date, heure, type) : si le
          // concurrent a été choisi/changé après l'enregistrement de l'affiche,
          // le nom « À déterminer » figé est remplacé par le nom réel.
          setElements(applyMatchData(stripLegacy(r.data), match));
          return;
        }
        // Template par défaut : on garde positions/tailles/couleurs et on
        // réinjecte le contenu dynamique (noms, date, heure, type) de CE match.
        const def = await API.get<PosterElement[]>('/poster-default-elements');
        if (def.data && def.data.length > 0) {
          setElements(applyMatchData(stripLegacy(def.data), match));
          return;
        }
      } catch {}
      setElements(buildDefaultElements(match));
    };
    loadElements().finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match.id]);

  // ── Drag / Resize / Rotate via pointer events (souris + tactile) ─────────
  const handlePointerMove = useCallback((e: PointerEvent) => {
    if (!dragRef.current) return;
    e.preventDefault();
    const sc = scaleRef.current;
    const { type, id, startMX, startMY, origX, origY, origW, origH, dir } = dragRef.current;

    if (type === 'rotate') {
      const cx = dragRef.current.centerX!;
      const cy = dragRef.current.centerY!;
      const angle = Math.atan2(e.clientY - cy, e.clientX - cx) * 180 / Math.PI + 90;
      setElements(prev => prev.map(el =>
        el.id === id ? { ...el, rotate: Math.round(angle) } : el
      ));
      return;
    }

    const dx = (e.clientX - startMX) / sc;
    const dy = (e.clientY - startMY) / sc;

    setElements(prev => prev.map(el => {
      if (el.id !== id) return el;
      if (type === 'drag') {
        return { ...el, x: Math.round(origX + dx), y: Math.round(origY + dy) };
      }
      let nx = el.x, ny = el.y, nw = el.width, nh = el.height;
      if (dir?.includes('e'))  nw = Math.max(30, Math.round(origW + dx));
      if (dir?.includes('s'))  nh = Math.max(20, Math.round(origH + dy));
      if (dir?.includes('w')) { nx = Math.round(origX + dx); nw = Math.max(30, Math.round(origW - dx)); }
      if (dir?.includes('n')) { ny = Math.round(origY + dy); nh = Math.max(20, Math.round(origH - dy)); }
      return { ...el, x: nx, y: ny, width: nw, height: nh };
    }));
  }, []);

  const handlePointerUp = useCallback(() => {
    dragRef.current = null;
    document.body.style.cursor = '';
  }, []);

  useEffect(() => {
    document.addEventListener('pointermove', handlePointerMove, { passive: false });
    document.addEventListener('pointerup', handlePointerUp);
    document.addEventListener('pointercancel', handlePointerUp);
    return () => {
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
      document.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [handlePointerMove, handlePointerUp]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  function startDrag(elId: string, e: React.PointerEvent) {
    e.preventDefault();
    e.stopPropagation();
    setSelectedId(elId);
    const el = elements.find(x => x.id === elId);
    if (!el) return;
    dragRef.current = {
      type: 'drag', id: elId,
      startMX: e.clientX, startMY: e.clientY,
      origX: el.x, origY: el.y, origW: el.width, origH: el.height,
    };
    document.body.style.cursor = 'move';
  }

  function startResize(elId: string, dir: HandleDir, e: React.PointerEvent) {
    e.preventDefault();
    e.stopPropagation();
    const el = elements.find(x => x.id === elId);
    if (!el) return;
    dragRef.current = {
      type: 'resize', id: elId, dir,
      startMX: e.clientX, startMY: e.clientY,
      origX: el.x, origY: el.y, origW: el.width, origH: el.height,
    };
  }

  function startRotate(elId: string, e: React.PointerEvent) {
    e.preventDefault();
    e.stopPropagation();
    const el = elements.find(x => x.id === elId);
    if (!el || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const sc = scaleRef.current;
    const centerX = rect.left + (el.x + el.width / 2) * sc;
    const centerY = rect.top  + (el.y + el.height / 2) * sc;
    dragRef.current = {
      type: 'rotate', id: elId,
      startMX: e.clientX, startMY: e.clientY,
      origX: el.x, origY: el.y, origW: el.width, origH: el.height,
      centerX, centerY,
    };
    document.body.style.cursor = 'crosshair';
  }

  function updateEl(id: string, patch: Partial<PosterElement>) {
    setElements(prev => prev.map(el => el.id === id ? { ...el, ...patch } : el));
  }

  function addTextElement() {
    const el: PosterElement = {
      id: genId(), type: 'text',
      x: 100, y: 500, width: 880, height: 100,
      content: 'Nouveau texte',
      font_family: 'Montserrat', font_size: 72, bold: true,
      color: '#ffffff', text_align: 'center',
      shadow_color: '#000000', shadow_offset: 4, opacity: 1,
    };
    setElements(prev => [...prev, el]);
    setSelectedId(el.id);
  }

  function deleteEl(id: string) {
    setElements(prev => prev.filter(el => el.id !== id));
    setSelectedId(null);
  }

  function moveEl(id: string, dir: 'up' | 'down') {
    setElements(prev => {
      const idx = prev.findIndex(el => el.id === id);
      if (idx < 0) return prev;
      const next = [...prev];
      const target = dir === 'up' ? idx - 1 : idx + 1;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  }

  async function handleSaveElements() {
    setSaved(false);
    try {
      await saveMatchElements(match.id, elements);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {}
  }

  // Rendu WYSIWYG : capture le conteneur pleine échelle (1080×1920) avec
  // html2canvas → PNG strictement identique à l'aperçu (polices, emojis, anneaux).
  // Sauvegarde aussi les éléments. Retourne true si succès.
  async function handleRender(): Promise<boolean> {
    setRendering(true);
    try {
      await saveMatchElements(match.id, elements);
      try { await (document as any).fonts?.ready; } catch {}
      await new Promise(res => setTimeout(res, 150));
      const node = fullRef.current;
      if (!node) throw new Error('Conteneur de rendu introuvable');
      const canvas = await html2canvas(node, {
        width: CANVAS_W, height: CANVAS_H,
        scale: 1, useCORS: true, backgroundColor: null, logging: false,
      });
      const blob: Blob | null = await new Promise(res => canvas.toBlob(res, 'image/png'));
      if (!blob) throw new Error('Export image échoué');
      const file = new File([blob], `poster_${match.id}.png`, { type: 'image/png' });
      const r = await uploadMatchPoster(match.id, file);
      const url = getMatchPosterUrl(r.data.poster_filename) + '?t=' + Date.now();
      setRenderUrl(url);
      onSaved(r.data.poster_filename);
      return true;
    } catch (err: any) {
      alert('Erreur : ' + (err?.response?.data?.detail || err.message));
      return false;
    } finally {
      setRendering(false);
    }
  }

  // Fermer = sauvegarder + générer le PNG puis quitter l'éditeur.
  async function handleCloseSaveGenerate() {
    setSelectedId(null);
    const ok = await handleRender();
    if (ok) onClose();
  }

  // Mobile : recentre le canvas sur l'élément sélectionné pour qu'il reste
  // visible au-dessus de la feuille de paramètres qui s'ouvre en bas.
  useEffect(() => {
    if (!isMobile || !selectedId) return;
    const el = elements.find(x => x.id === selectedId);
    const cont = centerScrollRef.current;
    if (!el || !cont) return;
    const elementCenterY = 8 + (el.y + el.height / 2) * previewScale;
    // Place l'élément à ~28% de la zone visible au-dessus de la feuille (~42% haut)
    const visibleBand = cont.clientHeight * 0.42;
    const target = Math.max(0, elementCenterY - visibleBand * 0.5);
    cont.scrollTo({ top: target, behavior: 'smooth' });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, isMobile, previewScale]);

  // Recharge le modèle ENREGISTRÉ par défaut (pas l'état initial), avec le
  // contenu dynamique de ce match.
  async function resetToDefaults() {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm('Revenir au modèle par défaut enregistré ?')) return;
    try {
      const def = await API.get<PosterElement[]>('/poster-default-elements');
      if (def.data && def.data.length > 0) {
        setElements(applyMatchData(def.data, match));
        setSelectedId(null);
        return;
      }
      // Pas de modèle enregistré : on demande explicitement avant de remettre le gabarit d'origine.
      // eslint-disable-next-line no-restricted-globals
      if (window.confirm("Aucun modèle par défaut n'est enregistré.\nRevenir au gabarit d'origine ?")) {
        setElements(buildDefaultElements(match));
        setSelectedId(null);
      }
    } catch (err: any) {
      const d = err?.response?.data?.detail;
      const msg = typeof d === 'string' ? d : (d ? JSON.stringify(d) : (err?.message || 'erreur réseau'));
      alert('Impossible de charger le modèle par défaut : ' + msg
        + '\n\nLes éléments actuels sont conservés.');
    }
  }

  async function handleSaveAsDefault() {
    try {
      // sauve aussi les éléments du match courant pour cohérence
      await saveMatchElements(match.id, elements);
      await API.put('/poster-default-elements', { elements });
      setDefaultSaved(true);
      setTimeout(() => setDefaultSaved(false), 2500);
    } catch (err: any) {
      alert('Impossible d\'enregistrer le modèle par défaut : '
        + (err?.response?.data?.detail || err?.message || 'erreur')
        + (err?.response?.status === 403 ? ' (réservé à l\'administrateur)' : ''));
    }
  }

  function addImageElement(imageUrl: string) {
    const el: PosterElement = {
      id: genId(), type: 'image',
      x: 200, y: 400, width: 680, height: 400,
      image_url: imageUrl, opacity: 1,
    };
    setElements(prev => [...prev, el]);
    setSelectedId(el.id);
  }

  async function handleImportImageFile(file?: File | null) {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Merci de choisir une image (jpg, png ou webp).');
      return;
    }
    try {
      const r = await uploadMatchEditorImage(match.id, file);
      addImageElement(r.data.image_url);
    } catch (err: any) {
      alert('Erreur import image : ' + (err?.response?.data?.detail || err.message));
    } finally {
      if (imageFileInputRef.current) imageFileInputRef.current.value = '';
    }
  }

  const selectedEl = elements.find(el => el.id === selectedId) || null;

  // ── Render ────────────────────────────────────────────────────────────────

  const panelBg = '#1a1a2e';
  const cardBg  = '#16213e';
  const accent  = '#00cfff';
  const border  = '#2a2a4a';
  const textCol = '#e8e8ff';
  const dimText = '#888aaa';

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 2000,
      background: 'rgba(0,0,0,0.92)',
      display: 'flex', flexDirection: 'column',
      // Respecte la barre d'état / encoche iOS (sinon l'en-tête avec le menu
      // sandwich passe sous la barre d'état iPhone et devient intappable).
      paddingTop: 'env(safe-area-inset-top, 0px)',
      paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      boxSizing: 'border-box',
    }}>
      {/* Conteneur caché pleine échelle (1080×1920) pour la capture html2canvas */}
      <div
        ref={fullRef}
        style={{
          position: 'fixed', left: -99999, top: 0,
          width: CANVAS_W, height: CANVAS_H,
          background: '#0a0814',
          backgroundImage: bgUrl ? `url(${bgUrl})` : undefined,
          backgroundSize: 'cover', backgroundPosition: 'center',
          overflow: 'hidden',
        }}
      >
        <div style={{
          position: 'absolute', inset: 0, zIndex: 0,
          background: bgUrl
            ? 'linear-gradient(to bottom, rgba(0,0,0,0.25) 0%, transparent 22%, transparent 58%, rgba(0,0,0,0.7) 100%)'
            : 'linear-gradient(to bottom, rgba(0,0,0,0.35) 0%, transparent 25%, transparent 60%, rgba(0,0,0,0.75) 100%)',
        }} />
        {elements.filter(el => !el.hidden).map(el => (
          <div key={el.id} style={{ position: 'absolute', left: 0, top: 0, zIndex: 1 }}>
            <ElementPreview el={el} selected={false} scale={1} match={match} logoUrl={logoUrl} editing={false} />
          </div>
        ))}
      </div>

      {/* Header */}
      {isMobile ? (
        <div style={{
          height: 56, boxSizing: 'border-box', background: panelBg, borderBottom: `1px solid ${border}`,
          display: 'flex', alignItems: 'center', padding: '0 10px', gap: 6, flexShrink: 0,
        }}>
          <button onClick={() => setShowListMobile(true)} style={{ ...btnStyle('#2a3a5a', textCol), fontSize: 22, padding: '9px 14px' }} title="Éléments">☰</button>
          <button onClick={resetToDefaults} style={{ ...btnStyle('#333', textCol), fontSize: 17, padding: '9px 11px' }} title="Modèle par défaut">↺</button>
          <button onClick={handleSaveAsDefault} style={{ ...btnStyle(defaultSaved ? '#1a5a30' : '#1a3a5a', defaultSaved ? '#7dffb0' : '#60cfff'), fontSize: 17, padding: '9px 11px' }} title="Définir par défaut">{defaultSaved ? '✓' : '⭐'}</button>
          <div style={{ flex: 1 }} />
          <button onClick={handleCloseSaveGenerate} disabled={rendering}
            style={{ ...btnStyle('#1a5a30', '#7dffb0', rendering), fontSize: 13, fontWeight: 700, padding: '10px 15px' }}>
            {rendering ? '⏳ …' : '✓ Terminer'}
          </button>
          <button onClick={onClose} style={{ ...btnStyle('#3a1a1a', '#ff8080'), fontSize: 16, padding: '9px 11px' }} title="Quitter sans générer">✕</button>
        </div>
      ) : (
        <div style={{
          height: 52, background: panelBg,
          borderBottom: `1px solid ${border}`,
          display: 'flex', alignItems: 'center',
          padding: '0 16px', gap: 12, flexShrink: 0,
        }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: textCol }}>
            🎨 Éditeur d'affiche — {match.streamer1_name} vs {match.streamer2_name}
          </span>
          <div style={{ flex: 1 }} />
          <button onClick={() => setShowAiPanel(true)} style={btnStyle('#3a1a5a', '#d8b4fe')}>🎨 IA Fond</button>
          <button onClick={resetToDefaults} style={btnStyle('#333', textCol)}>↺ Défaut</button>
          <button onClick={handleSaveAsDefault} style={btnStyle(defaultSaved ? '#1a5a30' : '#1a3a5a', defaultSaved ? '#7dffb0' : '#60cfff')}>
            {defaultSaved ? '✓ Modèle enregistré' : '⭐ Déf. par défaut'}
          </button>
          <button onClick={handleCloseSaveGenerate} disabled={rendering} style={btnStyle('#1a5a30', '#7dffb0', rendering)}>
            {rendering ? '⏳ Génération...' : '✓ Terminer (enregistre + PNG)'}
          </button>
          <button onClick={onClose} style={btnStyle('#3a1a1a', '#ff8080')}>✕ Quitter</button>
        </div>
      )}

      {/* Main content */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative' }}>

        {/* Backdrop du tiroir gauche (mobile) */}
        {isMobile && showListMobile && (
          <div onClick={() => setShowListMobile(false)}
            style={{ position: 'fixed', inset: 0, top: 'calc(56px + env(safe-area-inset-top, 0px))', background: 'rgba(0,0,0,0.5)', zIndex: 40 }} />
        )}

        {/* Left panel — element list */}
        <div style={isMobile ? {
          position: 'fixed', top: 'calc(56px + env(safe-area-inset-top, 0px))',
          bottom: 'env(safe-area-inset-bottom, 0px)', left: 0, width: '82%', maxWidth: 300,
          background: panelBg, borderRight: `1px solid ${border}`,
          display: 'flex', flexDirection: 'column', zIndex: 45,
          transform: showListMobile ? 'translateX(0)' : 'translateX(-105%)',
          transition: 'transform 0.25s ease', boxShadow: showListMobile ? '4px 0 24px rgba(0,0,0,0.5)' : undefined,
        } : {
          width: 200, background: panelBg, borderRight: `1px solid ${border}`,
          display: 'flex', flexDirection: 'column', overflowY: 'auto', flexShrink: 0,
        }}>
          <div style={{ padding: '10px 10px 6px', borderBottom: `1px solid ${border}` }}>
            {isMobile && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ color: textCol, fontWeight: 700, fontSize: 13 }}>Éléments</span>
                <button onClick={() => setShowListMobile(false)} style={{ background: 'none', border: 'none', color: dimText, fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>×</button>
              </div>
            )}
            <button onClick={addTextElement} style={{ ...btnStyle(accent, '#000'), width: '100%', fontSize: 12 }}>
              + Ajouter texte
            </button>
            <button onClick={() => imageFileInputRef.current?.click()} style={{ ...btnStyle('#2a5a3a', '#b7ffcf'), width: '100%', fontSize: 12, marginTop: 4 }}>
              + Importer image
            </button>
            <input
              ref={imageFileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              style={{ display: 'none' }}
              onChange={e => handleImportImageFile(e.target.files?.[0])}
            />
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '6px 6px' }}>
            {[...elements].reverse().map((el) => {
              return (
                <div
                  key={el.id}
                  onClick={() => { setSelectedId(el.id); if (isMobile) setShowListMobile(false); }}
                  style={{
                    padding: isMobile ? '10px 10px' : '6px 8px', marginBottom: 4,
                    background: selectedId === el.id ? 'rgba(0,207,255,0.15)' : cardBg,
                    border: `1px solid ${selectedId === el.id ? accent : border}`,
                    borderRadius: 6, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}
                >
                  {el.type === 'logo' ? (
                    <span style={{ width: 16, flexShrink: 0 }} />
                  ) : (
                    <input
                      type="checkbox"
                      checked={!el.hidden}
                      onClick={e => e.stopPropagation()}
                      onChange={e => { e.stopPropagation(); updateEl(el.id, { hidden: !e.target.checked }); }}
                      title={el.hidden ? "Afficher sur l'affiche" : "Masquer de l'affiche"}
                      style={{ width: 16, height: 16, flexShrink: 0, cursor: 'pointer', accentColor: accent }}
                    />
                  )}
                  <span style={{ fontSize: 14, opacity: el.hidden ? 0.4 : 1 }}>{elIcon(el.type)}</span>
                  <span style={{ fontSize: isMobile ? 13 : 11, color: textCol, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', opacity: el.hidden ? 0.4 : 1, textDecoration: el.hidden ? 'line-through' : 'none' }}>
                    {el.content || el.type}
                  </span>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <button onClick={e => { e.stopPropagation(); moveEl(el.id, 'up'); }}
                      style={{ ...iconBtn, fontSize: isMobile ? 12 : 9, padding: isMobile ? '2px 8px' : undefined }} title="Vers le haut">▲</button>
                    <button onClick={e => { e.stopPropagation(); moveEl(el.id, 'down'); }}
                      style={{ ...iconBtn, fontSize: isMobile ? 12 : 9, padding: isMobile ? '2px 8px' : undefined }} title="Vers le bas">▼</button>
                  </div>
                </div>
              );
            })}
          </div>
          {/* Fond — en bas de la liste : IA (prompt) + upload */}
          <div style={{ padding: '8px 10px', borderTop: `1px solid ${border}`, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button onClick={() => { setShowAiPanel(true); if (isMobile) setShowListMobile(false); }}
              style={{ ...btnStyle('#3a1a5a', '#d8b4fe'), width: '100%', fontSize: 12 }}>
              🎨 Fond IA (prompt)
            </button>
            <button onClick={() => bgFileInputRef.current?.click()} disabled={uploadingBg}
              style={{ ...btnStyle('#1a3a5a', '#a5d8ff'), width: '100%', fontSize: 12, opacity: uploadingBg ? 0.6 : 1 }}>
              {uploadingBg ? '⏳ Envoi…' : '🖼️ Importer un fond'}
            </button>
            <input ref={bgFileInputRef} type="file" accept="image/*" onChange={handleBgFile} style={{ display: 'none' }} />
          </div>
        </div>

        {/* Center — canvas preview */}
        <div
          ref={centerScrollRef}
          style={{
            flex: 1, display: 'flex',
            alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'center',
            background: '#0d0d1a', overflow: isMobile ? 'auto' : 'hidden', position: 'relative',
            // espace en bas pour remonter l'affiche au-dessus du panneau ; réduit
            // quand le panneau est replié (barre fine) pour voir toute l'affiche
            padding: isMobile ? (sheetExpanded ? '8px 0 64vh' : '8px 0 110px') : 0,
          }}>
          {loading ? (
            <div style={{ color: dimText, fontSize: 14 }}>Chargement…</div>
          ) : (
            <div
              ref={canvasRef}
              onClick={() => setSelectedId(null)}
              style={{
                position: 'relative',
                width: CANVAS_W * previewScale, height: CANVAS_H * previewScale,
                background: '#0a0814',
                backgroundImage: bgUrl ? `url(${bgUrl})` : undefined,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                overflow: 'hidden',
                boxShadow: '0 0 40px rgba(0,207,255,0.15)',
                cursor: 'default',
                flexShrink: 0,
                touchAction: 'none',
              }}
            >
              {/* Gradient overlay (always visible for readability) */}
              <div style={{
                position: 'absolute', inset: 0, zIndex: 0,
                background: bgUrl
                  ? 'linear-gradient(to bottom, rgba(0,0,0,0.25) 0%, transparent 22%, transparent 58%, rgba(0,0,0,0.7) 100%)'
                  : 'linear-gradient(to bottom, rgba(0,0,0,0.35) 0%, transparent 25%, transparent 60%, rgba(0,0,0,0.75) 100%)',
                pointerEvents: 'none',
              }} />

              {/* Elements (les éléments masqués via la case à cocher ne sont pas rendus) */}
              {elements.filter(el => !el.hidden).map(el => (
                <div
                  key={el.id}
                  style={{ position: 'absolute', left: 0, top: 0, zIndex: 1 }}
                  onClick={e => e.stopPropagation()}
                >
                  <ElementPreview
                    el={el} selected={selectedId === el.id}
                    scale={previewScale} match={match} logoUrl={logoUrl}
                    onPointerDown={e => startDrag(el.id, e)}
                  />
                  {selectedId === el.id && (
                    <>
                      {/* Resize handles + drag interceptor */}
                      <div
                        style={{
                          position: 'absolute',
                          left: el.x * previewScale, top: el.y * previewScale,
                          width: el.width * previewScale, height: el.height * previewScale,
                          zIndex: 11,
                          pointerEvents: 'none',
                        }}
                      >
                        <ResizeHandles
                          el={el} scale={previewScale} big={isMobile}
                          onHandleDown={(dir, e) => startResize(el.id, dir, e)}
                        />
                      </div>
                      {/* Rotation handle — circle above element center */}
                      <div
                        style={{
                          position: 'absolute',
                          left: (el.x + el.width / 2) * previewScale - (isMobile ? 13 : 7),
                          top: el.y * previewScale - (isMobile ? 40 : 22),
                          width: isMobile ? 26 : 14, height: isMobile ? 26 : 14,
                          borderRadius: '50%',
                          background: '#00cfff',
                          border: '2px solid #fff',
                          cursor: 'crosshair',
                          zIndex: 13,
                          pointerEvents: 'auto',
                          touchAction: 'none',
                          boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: isMobile ? 14 : 9,
                        }}
                        onPointerDown={e => startRotate(el.id, e)}
                        title="Faire pivoter"
                      >↻</div>
                      {/* Connector line from element to rotation handle */}
                      <div style={{
                        position: 'absolute',
                        left: (el.x + el.width / 2) * previewScale - 0.5,
                        top: el.y * previewScale - (isMobile ? 16 : 10),
                        width: 1, height: isMobile ? 18 : 12,
                        background: '#00cfff',
                        zIndex: 12,
                        pointerEvents: 'none',
                      }} />
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right panel — properties + preview (bottom sheet en mobile) */}
        <div style={isMobile ? {
          position: 'fixed', left: 0, right: 0, bottom: 0,
          maxHeight: sheetExpanded ? '62%' : undefined, background: panelBg, borderTop: `2px solid ${accent}`,
          borderRadius: '14px 14px 0 0', display: selectedEl ? 'flex' : 'none',
          flexDirection: 'column', overflowY: 'hidden', zIndex: 46,
          boxShadow: '0 -6px 24px rgba(0,0,0,0.5)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        } : {
          width: 260, background: panelBg, borderLeft: `1px solid ${border}`,
          display: 'flex', flexDirection: 'column', overflowY: 'auto', flexShrink: 0,
        }}>
          {/* En-tête mobile : barre fine repliée (on peut déplacer l'affiche
              librement) ou dépliée pour éditer les propriétés */}
          {isMobile && selectedEl && (
            <div onClick={() => setSheetExpanded(x => !x)}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', borderBottom: sheetExpanded ? `1px solid ${border}` : 'none', background: panelBg, cursor: 'pointer', flexShrink: 0 }}>
              <span style={{ fontSize: 15, color: accent }}>{sheetExpanded ? '▾' : '▸'}</span>
              <span style={{ color: textCol, fontWeight: 700, fontSize: 13, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{elIcon(selectedEl.type)} {selectedEl.content || selectedEl.type}</span>
              {!sheetExpanded && <span style={{ ...btnStyle(accent, '#000', false, 12), padding: '5px 12px' }}>Modifier</span>}
              <button onClick={e => { e.stopPropagation(); deleteEl(selectedEl.id); }} style={{ background: 'none', border: 'none', color: '#ff6060', fontSize: 17, cursor: 'pointer', padding: '2px 6px' }}>🗑</button>
              <button onClick={e => { e.stopPropagation(); setSelectedId(null); }} style={{ background: 'none', border: 'none', color: dimText, fontSize: 22, cursor: 'pointer', lineHeight: 1, padding: '0 4px' }}>×</button>
            </div>
          )}
          {isMobile && selectedEl && !sheetExpanded ? null : (
          <div style={{ overflowY: 'auto', minHeight: 0 }}>
          {/* Poster preview thumbnail */}
          {!isMobile && renderUrl && (
            <div style={{ padding: 10, borderBottom: `1px solid ${border}` }}>
              <p style={{ fontSize: 10, color: dimText, margin: '0 0 6px' }}>Dernier rendu PNG :</p>
              <img
                src={renderUrl} alt="Poster"
                style={{ width: '100%', borderRadius: 6, border: `1px solid ${border}` }}
              />
              <button
                onClick={() => downloadImage(renderUrl, `affiche_match_${match.id}.png`)}
                style={{ display: 'block', width: '100%', textAlign: 'center', fontSize: 11, color: accent, marginTop: 6, background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
              >
                📥 Télécharger
              </button>
            </div>
          )}

          {/* Element properties */}
          {selectedEl ? (
            <div style={{ padding: 12, flex: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <span style={{ fontSize: 12, color: accent, fontWeight: 700 }}>
                  {elIcon(selectedEl.type)} {selectedEl.type.toUpperCase()}
                </span>
                <button onClick={() => deleteEl(selectedEl.id)} style={btnStyle('#3a1010', '#ff6060', false, 10)}>
                  🗑 Suppr.
                </button>
              </div>

              {/* Position & size */}
              <Section title="Position & Taille">
                <Row label="X">
                  <NumInput value={selectedEl.x} onChange={v => updateEl(selectedEl.id, { x: v })} />
                </Row>
                <Row label="Y">
                  <NumInput value={selectedEl.y} onChange={v => updateEl(selectedEl.id, { y: v })} />
                </Row>
                <Row label="Largeur">
                  <NumInput value={selectedEl.width} onChange={v => updateEl(selectedEl.id, { width: v })} />
                </Row>
                <Row label="Hauteur">
                  <NumInput value={selectedEl.height} onChange={v => updateEl(selectedEl.id, { height: v })} />
                </Row>
                <Row label="Rotation">
                  <input type="range" min="-180" max="180" step="1"
                    value={selectedEl.rotate || 0}
                    onChange={e => updateEl(selectedEl.id, { rotate: parseFloat(e.target.value) })}
                    style={{ flex: 1 }}
                  />
                  <span style={{ fontSize: 10, color: dimText, width: 32, textAlign: 'right' }}>{selectedEl.rotate || 0}°</span>
                </Row>
                <Row label="Opacité">
                  <input type="range" min="0" max="1" step="0.05"
                    value={selectedEl.opacity ?? 1}
                    onChange={e => updateEl(selectedEl.id, { opacity: parseFloat(e.target.value) })}
                    style={{ width: '100%' }}
                  />
                  <span style={{ fontSize: 10, color: dimText }}>{Math.round((selectedEl.opacity ?? 1) * 100)}%</span>
                </Row>
              </Section>

              {/* Text-specific */}
              {selectedEl.type === 'text' && (
                <>
                  <Section title="Texte">
                    <textarea
                      value={selectedEl.content || ''}
                      onChange={e => updateEl(selectedEl.id, { content: e.target.value })}
                      rows={2}
                      style={{ width: '100%', background: cardBg, color: textCol, border: `1px solid ${border}`, borderRadius: 4, padding: '4px 6px', fontSize: 12, resize: 'vertical', boxSizing: 'border-box' }}
                    />
                  </Section>

                  <Section title="Police">
                    <Row label="Famille">
                      <select
                        value={selectedEl.font_family || 'DejaVu'}
                        onChange={e => updateEl(selectedEl.id, { font_family: e.target.value })}
                        style={selectStyle}
                      >
                        <option value="DejaVu" style={{ fontFamily: 'sans-serif' }}>DejaVu (défaut)</option>
                        {GOOGLE_FONTS.map(f => (
                          <option key={f} value={f} style={{ fontFamily: `'${f}', sans-serif`, fontSize: 15, padding: 4 }}>{f}</option>
                        ))}
                      </select>
                    </Row>
                    <Row label="Taille">
                      <NumInput value={selectedEl.font_size || 48} onChange={v => updateEl(selectedEl.id, { font_size: v })} min={8} max={400} />
                    </Row>
                    <Row label="Style">
                      <label style={{ color: textCol, fontSize: 11, cursor: 'pointer' }}>
                        <input type="checkbox" checked={!!selectedEl.bold}
                          onChange={e => updateEl(selectedEl.id, { bold: e.target.checked })} />
                        {' '}Gras
                      </label>
                      <label style={{ color: textCol, fontSize: 11, cursor: 'pointer', marginLeft: 8 }}>
                        <input type="checkbox" checked={!!selectedEl.italic}
                          onChange={e => updateEl(selectedEl.id, { italic: e.target.checked })} />
                        {' '}Italique
                      </label>
                    </Row>
                    <Row label="Alignement">
                      {(['left','center','right'] as const).map(a => (
                        <button key={a} onClick={() => updateEl(selectedEl.id, { text_align: a })}
                          style={{ ...iconBtn, background: selectedEl.text_align === a ? accent : '#333', color: selectedEl.text_align === a ? '#000' : textCol, marginRight: 3 }}>
                          {a === 'left' ? '⟵' : a === 'center' ? '≡' : '⟶'}
                        </button>
                      ))}
                    </Row>
                  </Section>

                  <Section title="Couleur">
                    <Row label="Texte">
                      <input type="color" value={selectedEl.color || '#ffffff'}
                        onChange={e => updateEl(selectedEl.id, { color: e.target.value })}
                        style={{ width: 40, height: 24, cursor: 'pointer', border: 'none', background: 'none' }} />
                      <span style={{ fontSize: 10, color: dimText, marginLeft: 4 }}>{selectedEl.color}</span>
                    </Row>
                    <Row label="Ombre">
                      <input type="color" value={selectedEl.shadow_color || '#000000'}
                        onChange={e => updateEl(selectedEl.id, { shadow_color: e.target.value })}
                        style={{ width: 40, height: 24, cursor: 'pointer', border: 'none', background: 'none' }} />
                      <button onClick={() => updateEl(selectedEl.id, { shadow_color: selectedEl.shadow_color ? undefined : '#000000' })}
                        style={{ ...iconBtn, fontSize: 10, marginLeft: 4 }}>
                        {selectedEl.shadow_color ? '✓' : '✗'}
                      </button>
                    </Row>
                    {selectedEl.shadow_color && (
                      <Row label="Décalage">
                        <NumInput value={selectedEl.shadow_offset || 4} onChange={v => updateEl(selectedEl.id, { shadow_offset: v })} min={0} max={30} />
                      </Row>
                    )}
                  </Section>
                </>
              )}

              {/* Avatar-specific */}
              {(selectedEl.type === 'avatar1' || selectedEl.type === 'avatar2') && (
                <Section title="Anneau">
                  <Row label="Couleur">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input type="color"
                        value={selectedEl.border_color || (selectedEl.type === 'avatar1' ? '#1e90ff' : '#ff3b3b')}
                        onChange={e => updateEl(selectedEl.id, { border_color: e.target.value })}
                        style={{ width: 40, height: 24, cursor: 'pointer', border: 'none', background: 'none' }} />
                      <button onClick={() => updateEl(selectedEl.id, { border_color: '#1e90ff' })}
                        style={{ ...btnStyle('#10306a', '#9cc7ff', false, 9) }}>Bleu</button>
                      <button onClick={() => updateEl(selectedEl.id, { border_color: '#ff3b3b' })}
                        style={{ ...btnStyle('#5a1010', '#ff9c9c', false, 9) }}>Rouge</button>
                    </div>
                  </Row>
                </Section>
              )}

              {/* Logo-specific — choix noir / blanc */}
              {selectedEl.type === 'logo' && (
                <Section title="Logo NS Live">
                  <Row label="Variante">
                    <div style={{ display: 'flex', gap: 6 }}>
                      {(['blanc', 'noir'] as const).map(v => {
                        const active = (selectedEl.logo_variant || 'blanc') === v;
                        return (
                          <button key={v} onClick={() => updateEl(selectedEl.id, { logo_variant: v })}
                            style={{
                              ...btnStyle(active ? accent : '#333', active ? '#000' : textCol, false, 10),
                              fontWeight: active ? 700 : 400,
                            }}>
                            {v === 'blanc' ? '🤍 Blanc' : '🖤 Noir'}
                          </button>
                        );
                      })}
                    </div>
                  </Row>
                  <div style={{ fontSize: 10, color: dimText, padding: '4px 8px' }}>
                    Blanc pour fond sombre, noir pour fond clair.
                  </div>
                </Section>
              )}

              {/* Image-specific */}
              {selectedEl.type === 'image' && (() => {
                const url = selectedEl.image_url || '';
                const isMatchType = url.includes('asset_match_');
                const isWhite = url.includes('_blanc');
                const swapVariant = (variant: 'noir' | 'blanc') =>
                  updateEl(selectedEl.id, {
                    image_url: url.replace(/_(noir|blanc)\.png/, `_${variant}.png`),
                  });
                return (
                  <Section title={isMatchType ? 'Type de match' : 'Image'}>
                    {isMatchType ? (
                      <Row label="Couleur">
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button onClick={() => swapVariant('blanc')}
                            style={{ ...btnStyle(isWhite ? accent : '#333', isWhite ? '#000' : textCol, false, 10), fontWeight: isWhite ? 700 : 400 }}>🤍 Blanc</button>
                          <button onClick={() => swapVariant('noir')}
                            style={{ ...btnStyle(!isWhite ? accent : '#333', !isWhite ? '#000' : textCol, false, 10), fontWeight: !isWhite ? 700 : 400 }}>🖤 Noir</button>
                        </div>
                      </Row>
                    ) : (
                      <>
                        <Row label="URL">
                          <input
                            value={url}
                            onChange={e => updateEl(selectedEl.id, { image_url: e.target.value })}
                            placeholder="https://..."
                            style={{ width: '100%', background: '#16213e', color: '#e8e8ff', border: '1px solid #2a2a4a', borderRadius: 4, padding: '2px 5px', fontSize: 10 }}
                          />
                        </Row>
                        <Row label="Noir & blanc">
                          <input type="checkbox" checked={!!selectedEl.grayscale}
                            onChange={e => updateEl(selectedEl.id, { grayscale: e.target.checked })}
                            style={{ cursor: 'pointer', width: 16, height: 16 }} />
                        </Row>
                      </>
                    )}
                  </Section>
                );
              })()}
            </div>
          ) : (
            <div style={{ padding: 20, color: dimText, fontSize: 12, textAlign: 'center' }}>
              <p>Cliquez sur un élément pour modifier ses propriétés.</p>
              <p style={{ marginTop: 8, fontSize: 11 }}>
                Glissez pour déplacer.<br />Tirez les poignées pour redimensionner.
              </p>
            </div>
          )}
          </div>
          )}
        </div>
      </div>

      {showAiPanel && (
        <AiBackgroundPanel
          matchId={match.id}
          isAdmin={!!isAdmin}
          currentBg={bgFilename}
          onClose={() => setShowAiPanel(false)}
          onApply={async (filename) => {
            try {
              await setMatchBackgroundFilename(match.id, filename);
              setBgFilename(filename);
              onBackgroundUpdated?.(filename);
            } catch {}
          }}
        />
      )}
    </div>
  );
}

// ── Panneau IA — génération de fond Pollinations ────────────────────────────

function AiBackgroundPanel({ matchId, isAdmin, currentBg, onClose, onApply }: {
  matchId: number;
  isAdmin: boolean;
  currentBg: string | null;
  onClose: () => void;
  onApply: (filename: string) => Promise<void> | void;
}) {
  const [prompt, setPrompt] = useState('');
  const [styles, setStyles] = useState<PollinationsStyle[]>([]);
  const [style, setStyle] = useState('realiste');
  const [images, setImages] = useState<MatchAiImage[]>([]);
  const [limit, setLimit] = useState<number | null>(null);
  const [used, setUsed] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [appliedUrl, setAppliedUrl] = useState<string | null>(
    currentBg ? getMatchPosterUrl(currentBg) : null
  );

  const reload = useCallback(async () => {
    try {
      const r = await getMatchAiImages(matchId);
      setImages(r.data.images);
      setLimit(r.data.limit);
      setUsed(r.data.used);
    } catch {}
  }, [matchId]);

  useEffect(() => {
    getPollinationsStyles().then(r => {
      setStyles(r.data.styles);
      if (r.data.styles.length) setStyle(r.data.styles[0].key);
    }).catch(() => {});
    reload();
  }, [reload]);

  const quotaReached = !isAdmin && limit != null && used >= limit;

  const handleGenerate = async () => {
    if (!prompt.trim() || generating) return;
    setGenerating(true); setErr(null);
    try {
      const r = await generateMatchBackground(matchId, prompt.trim(), style);
      setUsed(r.data.used);
      setLimit(r.data.limit);
      await reload();
      // Applique automatiquement la dernière image générée
      await onApply(r.data.filename);
      setAppliedUrl(r.data.image_url);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || 'Échec de la génération');
    } finally {
      setGenerating(false);
    }
  };

  const overlay: React.CSSProperties = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 3000,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
  };
  const panel: React.CSSProperties = {
    background: '#14142a', border: '1px solid #2a2a4a', borderRadius: 14,
    width: '100%', maxWidth: 720, maxHeight: '90vh', display: 'flex',
    flexDirection: 'column', color: '#e8e8ff', boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={panel} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #2a2a4a', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 18 }}>🎨</span>
          <div style={{ flex: 1, fontSize: 15, fontWeight: 700 }}>Générer un fond par IA</div>
          {!isAdmin && limit != null && (
            <span style={{ fontSize: 12, fontWeight: 700, color: quotaReached ? '#ff8080' : '#8ee6a0', background: 'rgba(255,255,255,0.06)', borderRadius: 999, padding: '3px 10px' }}>
              {used}/{limit} images
            </span>
          )}
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#888aaa', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 18, display: 'flex', flexDirection: 'column', gap: 14, minHeight: 0 }}>
          {/* Formulaire de génération */}
          {quotaReached ? (
            <div style={{ background: '#3a1a1a', border: '1px solid #5a2a2a', color: '#ffb0b0', borderRadius: 8, padding: '10px 12px', fontSize: 13 }}>
              Limite de {limit} images atteinte pour ce match. Choisis ci-dessous parmi les images déjà générées.
            </div>
          ) : (
            <>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#b8b8d8', marginBottom: 5 }}>Description de l'image</label>
                <textarea value={prompt} onChange={e => setPrompt(e.target.value)} rows={3}
                  placeholder="Ex : deux guerriers face à face dans une arène en feu, ambiance épique"
                  style={{ width: '100%', boxSizing: 'border-box', background: '#0d0d1a', color: '#e8e8ff', border: '1px solid #2a2a4a', borderRadius: 8, padding: '9px 11px', fontSize: 13, resize: 'vertical', fontFamily: 'inherit' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#b8b8d8', marginBottom: 5 }}>Style</label>
                <select value={style} onChange={e => setStyle(e.target.value)}
                  style={{ width: '100%', background: '#0d0d1a', color: '#e8e8ff', border: '1px solid #2a2a4a', borderRadius: 8, padding: '9px 11px', fontSize: 13 }}>
                  {styles.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              </div>
              {err && <div style={{ color: '#ff8080', fontSize: 12 }}>{err}</div>}
              <button onClick={handleGenerate} disabled={!prompt.trim() || generating}
                style={{ background: prompt.trim() && !generating ? 'linear-gradient(135deg,#7c3aed,#a855f7)' : '#3a3a5a', color: '#fff', border: 'none', borderRadius: 8, padding: '11px 20px', fontSize: 14, fontWeight: 700, cursor: prompt.trim() && !generating ? 'pointer' : 'not-allowed', alignSelf: 'flex-start' }}>
                {generating ? '⏳ Génération en cours… (~15 s)' : '✨ Générer l\'image'}
              </button>
            </>
          )}

          {/* Galerie des images déjà générées pour ce match */}
          {images.length > 0 && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#b8b8d8', marginBottom: 8, marginTop: 4 }}>
                Images générées pour ce match — clique pour l'appliquer comme fond
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 10 }}>
                {images.map(img => {
                  const isApplied = appliedUrl === img.image_url;
                  return (
                    <button key={img.id} onClick={async () => { await onApply(img.image_url.split('/').pop() || ''); setAppliedUrl(img.image_url); }}
                      title={`${img.prompt} (${img.style})`}
                      style={{ padding: 0, border: `2px solid ${isApplied ? '#00cfff' : '#2a2a4a'}`, borderRadius: 8, overflow: 'hidden', cursor: 'pointer', background: '#0d0d1a', position: 'relative', aspectRatio: '9/16' }}>
                      <img src={img.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                      {isApplied && (
                        <span style={{ position: 'absolute', top: 4, right: 4, background: '#00cfff', color: '#000', fontSize: 10, fontWeight: 700, borderRadius: 4, padding: '1px 5px' }}>✓ Fond</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Style helpers ──────────────────────────────────────────────────────────

function btnStyle(bg: string, color: string, disabled = false, fs = 12): React.CSSProperties {
  return {
    background: disabled ? '#333' : bg,
    color: disabled ? '#666' : color,
    border: 'none', borderRadius: 6, padding: '6px 12px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: fs, fontWeight: 600, whiteSpace: 'nowrap',
  };
}

const iconBtn: React.CSSProperties = {
  background: '#2a2a4a', color: '#ccc',
  border: 'none', borderRadius: 3, padding: '2px 5px',
  cursor: 'pointer', fontSize: 11,
};

const selectStyle: React.CSSProperties = {
  background: '#16213e', color: '#e8e8ff',
  border: '1px solid #2a2a4a', borderRadius: 4,
  padding: '3px 6px', fontSize: 11, width: '100%',
};

function elIcon(type: string) {
  switch (type) {
    case 'text': return '📝';
    case 'avatar1': return '👤';
    case 'avatar2': return '👤';
    case 'logo': return '🏷';
    case 'image': return '🖼';
    default: return '📦';
  }
}

// ── Small UI components ────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 10, color: '#888aaa', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
        {title}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {children}
      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minHeight: 24 }}>
      <span style={{ fontSize: 10, color: '#888aaa', width: 58, flexShrink: 0 }}>{label}</span>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 4 }}>{children}</div>
    </div>
  );
}

function NumInput({ value, onChange, min = -9999, max = 9999 }: {
  value: number; onChange: (v: number) => void; min?: number; max?: number;
}) {
  return (
    <input
      type="number" value={value} min={min} max={max}
      onChange={e => onChange(parseInt(e.target.value) || 0)}
      style={{ width: '100%', background: '#16213e', color: '#e8e8ff', border: '1px solid #2a2a4a', borderRadius: 4, padding: '2px 5px', fontSize: 11 }}
    />
  );
}
